import React from 'react';
import { motion } from 'framer-motion';
import { Cpu, GitBranch, Activity } from 'lucide-react';

const ProjectCard = ({ project, index }) => {
    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6, delay: index * 0.15, ease: [0.25, 0.1, 0.25, 1.0] }}
            className="group relative flex flex-col h-full bg-surface border border-border rounded-2xl overflow-hidden hover:border-primary/30 hover:shadow-card-hover transition-all duration-500"
        >
            {/* Top Bar / Decor */}
            <div className="h-1 w-full bg-gradient-to-r from-primary/30 to-transparent group-hover:from-primary group-hover:to-secondary/50 transition-all duration-500" />

            <div className="p-8 flex flex-col h-full">
                {/* Header */}
                <div className="flex justify-between items-start mb-6">
                    <div className="p-3 bg-surface-highlight rounded-xl border border-border text-primary group-hover:bg-primary group-hover:text-white transition-colors duration-300">
                        <project.icon size={24} />
                    </div>
                    {/* Badge */}
                    <div className="px-3 py-1 rounded-full bg-primary/10 border border-primary/20 text-xs text-primary font-mono">
                        {project.metric}
                    </div>
                </div>

                {/* Content */}
                <h3 className="text-2xl font-display font-bold text-text mb-3 group-hover:text-primary transition-colors duration-300">
                    {project.title}
                </h3>
                <p className="text-text-secondary text-sm leading-relaxed mb-6 flex-grow">
                    {project.description}
                </p>

                {/* Tech Stack */}
                <div className="flex flex-wrap gap-2 pt-6 border-t border-border">
                    {project.tech.map((tech, i) => (
                        <span key={i} className="text-xs font-mono text-text-tertiary px-2.5 py-1 rounded-md bg-surface-highlight border border-border">
                            {tech}
                        </span>
                    ))}
                </div>
            </div>

            {/* Hover Effect Overlay */}
            <div className="absolute inset-0 bg-gradient-to-b from-transparent to-primary/3 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
        </motion.div>
    );
};

const FeaturedWork = () => {
    const projects = [
        {
            title: "Automated Config Generator",
            description: "Engineered Python/Node.js tooling that reduced 3-hour manual partner configuration process to under 10 minutes across 12 LATAM markets.",
            metric: "95% Time Reduction",
            icon: Cpu,
            tech: ["Python", "Node.js", "JSON Automation"]
        },
        {
            title: "DevRev Orchestration",
            description: "Sole administrator for 200+ ops professionals. Engineered SLA workflows, routing automation, and macro systems across 4 global regions.",
            metric: "200+ Users",
            icon: GitBranch,
            tech: ["DevRev", "SLA Workflows", "Routing Logic"]
        },
        {
            title: "Data Pipeline Diagnostics",
            description: "Diagnosed and resolved failures across AWS Lambda, Kinesis, and CloudWatch. Maintained 4-hour SLA targets for real-time data ingestion.",
            metric: "4h SLA Target",
            icon: Activity,
            tech: ["AWS Lambda", "Kinesis", "CloudWatch", "Postman"]
        }
    ];
    return (
        <section id="work" className="py-24 md:py-32 bg-background relative">
            <div className="max-w-6xl mx-auto px-6">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    className="mb-16 md:flex md:items-end md:justify-between"
                >
                    <div className="max-w-2xl">
                        <span className="text-primary font-mono text-sm tracking-wider uppercase mb-3 block">
                            Key Projects
                        </span>
                        <h2 className="text-3xl md:text-5xl font-display font-bold text-text tracking-tight">
                            Engineering Impact
                        </h2>
                    </div>
                </motion.div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    {projects.map((project, index) => (
                        <ProjectCard key={index} project={project} index={index} />
                    ))}
                </div>
            </div>
        </section>
    );
};

export default FeaturedWork;
